import type {
  ExposureEvent,
  PolarHeartRateFields,
  PolarProductId,
  RrSource,
} from '@types';

export type { PolarProductId, RrSource, PolarHeartRateFields };

export type SessionStatus =
  | 'queued'
  | 'initializing'
  | 'in-progress'
  | 'completed'
  | 'loading';

// questi sono base
export interface HeartRateEventBase {
  hr: number;
  timestamp: number;
  deviceCode?: string;
  id?: string;
}
export interface HeartRateEvent
  extends HeartRateEventBase, PolarHeartRateFields {
  hrv?: number | null;
  lf?: number | null;
  hf?: number | null;
  type?: 'gap';
  repair?: {
    type: 'gap' | 'duplicate';
    summary: string;
    sourceCount?: number;
    fromTimestamp?: number;
    toTimestamp?: number;
  };
}
export interface DeviceConfiguration {
  baseline: number;
  baselineDuration?: number;
  config: DeviceConfig;
  created_at: string;
  device_id: string;
  hrData: HeartRateEvent[];
  hrData_repaired?: HeartRateEvent[] | string | null;
  id: number;
  patient_id: number;
  results: DeviceResults[];
  scenario: ScenarioType;
  status: SessionStatus;
  type: DeviceConfigurationType;
  updated_at: string;
  user_id: string;
  ended_at?: string | null;
  sensor_id?: number | null;
  answers?: string | null;
  started_at?: string | null;
  exposureEvents?: ExposureEvent[];
}

export type DeviceConfigurationApi = DeviceConfiguration & {
  patientCode: string;
  deviceName: string;
  deviceCode: string;
  connectedDevices: ConnectedDevice[];
  connectedTo?: string;
  results?: any; //TODO create type
  video?: VideoInfo;
  /** Present on list/summary responses when `config` is omitted. */
  videoId?: number | null;
  hasHrDataRepaired?: boolean;
};

export type VideoInfo = {
  id: number;
  name: string;
  version: number;
  url: string;
  category: string;
  size: number;
  duration: number;
  type: string;
  product_name?: string;
  product_image?: string;
  product_description?: string;
};

export type ConnectedDevice = {
  id: number;
  name: string;
  code: string;
  type: string;
};

export type DeviceCurrentState = 'p2' | 'p1' | 'p0' | 'p-1' | 'p-2';
export interface DeviceConfig {
  duration?: number;
  maxDuration?: number;
  currentState?: DeviceCurrentState;
  variation?: number;
  baseline?: number;
  totalDuration?: number;
  [key: string]: any; //TODO: extends type with real keys
}
export interface DeviceResults {
  date: string;
  state: string;
}

export type DeviceConfigurationType =
  | 'performance'
  | 'exposure'
  | 'body_image'
  | 'wellbeing'
  | 'psychology'
  | 'corporate'
  | 'situations'
  | 'mentalization';

export type ScenarioType = string; // TODO: add type

/**
 * Key inside the `config` JSON blob for a duplicate copy of exposure events.
 * Used when completing a session so data is still available if the API omits top-level `exposureEvents` on read.
 */
export const EXPOSURE_EVENTS_CONFIG_KEY = '_exposureEvents';

/**
 * Returns config without result data (_exposureEvents). Use when saving so the config column stores only initial config.
 */
export function getInitialConfigOnly(config: unknown): Record<string, unknown> {
  if (config == null) return {};
  const obj =
    typeof config === 'string'
      ? (() => {
          try {
            return JSON.parse(config) as Record<string, unknown>;
          } catch {
            return {};
          }
        })()
      : typeof config === 'object' && config !== null
        ? { ...(config as Record<string, unknown>) }
        : {};
  delete obj[EXPOSURE_EVENTS_CONFIG_KEY];
  return obj;
}

/**
 * JSON string for PATCH `config` when completing a session: initial config plus embedded exposure events when any.
 */
export function buildCompletedSessionConfigJson(
  existingConfig: unknown,
  exposureEvents: ExposureEvent[]
): string {
  const initialOnly = getInitialConfigOnly(existingConfig);
  if (exposureEvents.length === 0) {
    return JSON.stringify(initialOnly);
  }
  return JSON.stringify({
    ...initialOnly,
    [EXPOSURE_EVENTS_CONFIG_KEY]: exposureEvents,
  });
}
